import React, { useState, useRef, ReactNode } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { HeaderTabItem } from 'components/LandingPage/HeaderTabs';
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from 'react-router-dom';
import { ExpandMore, Check } from '@mui/icons-material';

interface HoverMenuItem {
  label: ReactNode;
  path: string;
  activeMatch?: (pathname: string) => boolean;
}

interface DynamicHoverMenuProps {
  label: ReactNode;
  items: HoverMenuItem[] | HeaderTabItem[];
  disabled?: boolean;
}

const DynamicHoverMenu: React.FC<DynamicHoverMenuProps> = ({ label, items, disabled = false }) => {
  const theme = useTheme();
  const colors = theme.palette;
  const navigate = useNavigate();
  const location = useLocation();
  const activeColor = colors.textLight.dark;
  const inactiveColor = colors.textLight.secondary;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 150);
  };

  const handleMenuEnter = () => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const open = Boolean(anchorEl) && !disabled;

  const isItemActive = (item: HoverMenuItem | HeaderTabItem) => {
    return item.activeMatch
      ? item.activeMatch(location.pathname)
      : location.pathname === item.path;
  };

  return (
    <Box sx={{ display: 'inline-block' }} className="dynamic-hover-menu">
      <Box
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontWeight: 'inherit',
          color: open ? activeColor : inactiveColor,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          opacity: disabled ? 0.6 : 1
        }}
      >
        {label}
        <ExpandMore
          sx={{
            color: open ? activeColor : inactiveColor,
            '& path': {
              fill: open ? activeColor : inactiveColor,
            }
          }}
        />
      </Box>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          onMouseEnter: handleMenuEnter,
          onMouseLeave: handleClose,
          sx: {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
          }
        }}
        sx={{
          pointerEvents: 'none',
          "& .MuiMenu-paper": {
            color: colors.DropdownV2.menuItemText,
            backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
            p: '0 !important',
            margin: '0 !important'
          }
        }}
        slotProps={{
          paper: {
            sx: {
              pointerEvents: disabled ? 'none' : 'auto',
              mt: '0 !important',
              minWidth: 250,
              backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
              px: '0 !important',
              py: '0 !important'
            }
          }
        }}
      >
        {items.map((item, index) => {
          const active = isItemActive(item);

          return (
            <MenuItem
              key={`${index}`}
              onClick={(event) => {
                if (disabled) {
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }

                event.stopPropagation();
                setAnchorEl(null);
                navigate(item.path);
              }}
              sx={{
                color: `${colors.DropdownV2.menuItemText} !important`,
                backgroundColor: active ? `${colors.DropdownV2.menuItemSelectedBg} !important` : 'transparent !important',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                cursor: disabled ? 'not-allowed' : 'pointer',
                pointerEvents: disabled ? 'none' : 'auto',
                "&:hover": {
                  color: `${colors.DropdownV2.menuItemText} !important`,
                  backgroundColor: `${colors.DropdownV2.menuItemHover} !important`,
                },
                "&.MuiMenuItem-root": {
                  minHeight: '36px',
                  px: "30px",
                  py: "8px"
                },
                '& .MuiTypography-root': {
                  color: `${colors.DropdownV2.menuItemText} !important`,
                }
              }}
            >
              <Typography variant='dropdownListText'>{item.label}</Typography>

              {active && (
                <Check
                  sx={{
                    fontSize: '22px',
                    color: `${activeColor} !important`
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default DynamicHoverMenu;
